import * as banterApi from '../../api/banter';

/**
 * Where one Banterboard's posts come from, and where a new one goes.
 *
 * There are three routes and they answer differently: the league route returns a bare
 * array, the game route returns `{ posts, open }` because a standalone game's board
 * closes when the game ends, and the house route returns a bare array that nobody may
 * write to at all. Normalising that here means the component renders one shape and
 * nothing downstream has to know which route it came from.
 *
 * <p>`postable` is the third answer and is not the same as `open`. A closed game board
 * says so — "that's full time, the board's closed" — because it was a conversation and
 * has ended. The House board was never a conversation: it is the platform talking, one
 * post reaching every account, and read-only for players by design while there is no
 * moderation queue behind it. Saying "closed" there would describe something that never
 * happened.
 *
 * <p>`targets` is who and what may be named on the board - the same pair of routes, and
 * asked of the server for the same reason the posts are. The portal used to fill the
 * player picker from whatever roster the page happened to be holding, which is a
 * different shape on a Game than on a League, and the team picker from every team on the
 * platform. Both are properties of the board, so both are answered by the board.
 *
 * <p>Worth being clear about what asking by game actually does: a game inside a league
 * is handed <em>the league's board</em>, the same thread the League page shows. Only a
 * standalone game has one of its own. That resolution happens on the server, so this
 * file does not need to know which kind of game it is holding — and must not try to
 * guess, because the answer changes when a game is converted into a league.
 */
export function boardSource({ tournamentId, gameId, house = false }) {
  if (house) {
    return {
      key: 'house',
      // Never closed and never open: there is no composer to decide about.
      load: async () => ({ posts: await banterApi.getHouseBoard(), open: false }),
      postable: false,
      post: null,
      // Nobody may name anybody here, so there is nothing to offer. Answered rather
      // than left undefined, because the board fetches this unconditionally and a
      // missing function would be a crash rather than an empty picker.
      targets: async () => ({ players: [], teams: [] }),
    };
  }

  if (gameId != null) {
    return {
      key: `game:${gameId}`,
      postable: true,
      load: async () => {
        const data = await banterApi.getGameBoard(gameId);
        return { posts: data.posts ?? [], open: data.open !== false };
      },
      post: (choice) => banterApi.postToGameBoard(gameId, choice),
      targets: () => banterApi.getGameBoardTargets(gameId),
    };
  }

  return {
    key: `league:${tournamentId}`,
    postable: true,
    // A league board outlives any one of its games, so it is never closed.
    load: async () => ({ posts: await banterApi.getLeagueBoard(tournamentId), open: true }),
    post: (choice) => banterApi.postPhrase(tournamentId, choice),
    targets: () => banterApi.getLeagueBoardTargets(tournamentId),
  };
}
